import { Clock, ArrowUpRight, Calendar as CalendarIcon } from "lucide-react";
import { SERVICES } from "../sections";
import { DatePicker } from "./DatePicker";

const inputClass = "w-full rounded-2xl border border-[var(--border)] bg-[var(--bg)] px-5 py-4 text-[var(--text-dark)] placeholder:text-[var(--text-muted)]/60 outline-none transition-colors focus:border-[var(--mint-dark)] focus:bg-white";

function Field({ label, icon: Icon, span, children }) {
  return (
    <div className={span ? "sm:col-span-2" : ""}>
      <label className="mb-2 flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--text-dark)" }}>
        {Icon && <Icon size={15} />} {label}
      </label>
      {children}
    </div>
  );
}

function Notice({ tone, children, testId }) {
  const styles = tone === "warn"
    ? { bg: "#FEF3C7", fg: "#92400E" }
    : { bg: "var(--mint-light)", fg: "var(--mint-dark)" };
  return (
    <div className="mt-6 flex items-start gap-3 rounded-2xl p-4" style={{ background: styles.bg }} data-testid={testId}>
      <Clock size={18} className="mt-0.5 shrink-0" style={{ color: styles.fg }} />
      <p className="text-sm" style={{ color: styles.fg }}>{children}</p>
    </div>
  );
}

function slotPlaceholder(fecha, slots, dayTaken) {
  if (!fecha) return "Primero elige una fecha";
  if (dayTaken) return "Ese día ya está reservado";
  return slots.length === 0 ? "Sin horarios libres ese día" : "Selecciona un horario";
}

export function BookingForm({ form, update, onDateChange, availability, loading, onSubmit, visible }) {
  const { hasAvailability, availableSlots, dayTaken, month, setMonth, availableDates, loadingDates } = availability;
  return (
    <form onSubmit={onSubmit} data-testid="booking-form" style={{ display: visible ? "block" : "none" }}>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Nombre completo *" span>
          <input data-testid="input-nombre" className={inputClass} value={form.nombre} onChange={update("nombre")} placeholder="Tu nombre completo" />
        </Field>
        <Field label="Teléfono / WhatsApp *">
          <input data-testid="input-telefono" className={inputClass} value={form.telefono} onChange={update("telefono")} placeholder="+52 ..." />
        </Field>
        <Field label="Correo electrónico *">
          <input data-testid="input-correo" type="email" className={inputClass} value={form.correo} onChange={update("correo")} placeholder="correo@ejemplo.com" />
        </Field>
        <Field label="Servicio de interés *" span>
          <select data-testid="select-servicio" className={inputClass} value={form.servicio} onChange={update("servicio")}>
            <option value="">Selecciona un servicio</option>
            {SERVICES.map((s) => <option key={s.title} value={s.title}>{s.title}</option>)}
          </select>
        </Field>
        <Field label="Fecha preferida *" icon={CalendarIcon}>
          <DatePicker value={form.fecha_preferida} onChange={onDateChange} availableDates={availableDates} month={month}
            onMonthChange={setMonth} disabled={!hasAvailability} loading={loadingDates} className={inputClass} />
        </Field>
        <Field label="Horario preferido *" icon={Clock}>
          <select data-testid="input-horario" disabled={!hasAvailability || !form.fecha_preferida} className={inputClass + " disabled:opacity-50"} value={form.horario_preferido} onChange={update("horario_preferido")}>
            <option value="">{slotPlaceholder(form.fecha_preferida, availableSlots, dayTaken)}</option>
            {availableSlots.map((s) => <option key={s} value={s}>{s} hrs</option>)}
          </select>
        </Field>
        <Field label="Comentarios adicionales (opcional)" span>
          <textarea data-testid="input-comentarios" rows={3} className={inputClass + " resize-none"} value={form.comentarios} onChange={update("comentarios")} placeholder="Cuéntame lo que necesites..." />
        </Field>
      </div>

      {dayTaken && form.fecha_preferida && (
        <Notice tone="warn" testId="day-taken-notice">
          <strong>Ese día ya tiene una cita reservada.</strong> Solo atendemos a un paciente por día; elige otra fecha disponible.
        </Notice>
      )}

      {!hasAvailability && (
        <Notice tone="warn" testId="no-availability-notice">
          <strong>Por el momento no hay horarios disponibles.</strong> Vuelve pronto o escríbenos por WhatsApp para más información.
        </Notice>
      )}

      <Notice>
        <strong>Aviso importante:</strong> Se requiere disponibilidad de horario para asistir al tratamiento. Tu cita se confirma al completar el pago de $200 MXN de la primera consulta.
      </Notice>

      <p className="mt-6 text-xs leading-relaxed" style={{ color: "var(--text-muted)" }} data-testid="privacy-legend">
        Al hacer clic en "Pagar $200 MXN y reservar", aceptas nuestro{" "}
        <a href="/aviso-de-privacidad" target="_blank" rel="noopener noreferrer" data-testid="privacy-link"
          className="font-semibold underline" style={{ color: "var(--mint-dark)" }}>
          Aviso de Privacidad
        </a>.
      </p>

      <button type="submit" disabled={loading || !hasAvailability} data-testid="booking-submit-button"
        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-4 text-base font-semibold text-white transition-transform hover:scale-[1.01] disabled:opacity-60"
        style={{ background: "var(--mint-dark)" }}>
        {loading ? "Preparando pago seguro..." : "Pagar $200 MXN y reservar"}
        {!loading && <ArrowUpRight size={18} />}
      </button>
    </form>
  );
}
